// Curated "test bank" of Motic (80x) microscope images with PRE-BAKED answers, so
// students without a microscope can practise as a game: guess, get instant
// match / try-again feedback. No model call — each sample carries ground truth.
//
// Two game modes, driven by the stain:
//   • Gram stain           → guess Gram (+/-) and shape (cocci/bacillus)  [bacteria]
//   • Methylene blue        → guess the organism KIND (bacteria/yeast/mould)
// Gram / shape / kind are microbiological ground truth, so the reveal shows 100%.
//
// ── HOW THE LIST IS BUILT ───────────────────────────────────────────────────────
// Instead of hand-listing 200 rows, we expand SPECIES[].count into entries with
// predictable public URLs. So to scale up: (1) copy that many good Motic tiles into
// the PUBLIC bucket named `<key>_80X_<NNN>.jpg` (NNN zero-padded from 001), and
// (2) set `count` to how many you uploaded. The training bucket `bioeureka-images`
// is private (WORM); the app loads by public URL, so the tiles must live in
// s3://bioeureka-public-assets/testbank/.

export type OrganismKind = 'bacteria' | 'yeast' | 'mould';

export interface TestBankSample {
  id: string;
  imageUrl: string;
  coloration: 'gram' | 'methylene_blue';
  organism?: string;
  results: {
    gram: 'pos' | 'neg' | 'uncertain';
    shape: 'cocci' | 'bacillus' | 'uncertain';
    kind: OrganismKind;
    gram_proba?: number; // optional — defaults to 1 (100%, ground truth)
    shape_proba?: number;
  };
}

const S3 = 'https://bioeureka-public-assets.s3.ca-central-1.amazonaws.com/testbank';

interface SpeciesDef {
  key: string;              // filename stem, e.g. 'ecoli' -> ecoli_80X_001.jpg
  organism?: string;        // shown after the guess (omit for generic moulds)
  coloration: 'gram' | 'methylene_blue';
  gram?: 'pos' | 'neg';     // bacteria only
  shape?: 'cocci' | 'bacillus';
  kind: OrganismKind;
  count: number;            // how many tiles are published for this key
}

// `key` + `count` must match the files published by deploy/testbank/publish_testbank.py
// (it uploads <key>_80X_<NNN>.jpg). Totals: 6×25 Gram bacteria (150) + 20 yeast +
// 30 mould + 20 MB bacteria (blue) = 220 samples.
const SPECIES: SpeciesDef[] = [
  // ── Gram stain → guess Gram + shape (the 6 target bacteria) ────────────────
  { key: 'saureus',        organism: 'Staphylococcus aureus',    coloration: 'gram', gram: 'pos', shape: 'cocci',    kind: 'bacteria', count: 25 },
  { key: 'sagalactiae',    organism: 'Streptococcus agalactiae', coloration: 'gram', gram: 'pos', shape: 'cocci',    kind: 'bacteria', count: 25 },
  { key: 'lmonocytogenes', organism: 'Listeria monocytogenes',   coloration: 'gram', gram: 'pos', shape: 'bacillus', kind: 'bacteria', count: 25 },
  { key: 'ecoli',          organism: 'Escherichia coli',         coloration: 'gram', gram: 'neg', shape: 'bacillus', kind: 'bacteria', count: 25 },
  { key: 'senterica',      organism: 'Salmonella enterica',      coloration: 'gram', gram: 'neg', shape: 'bacillus', kind: 'bacteria', count: 25 },
  { key: 'paeruginosa',    organism: 'Pseudomonas aeruginosa',   coloration: 'gram', gram: 'neg', shape: 'bacillus', kind: 'bacteria', count: 25 },
  // ── Methylene blue → guess the kind (bacteria / yeast / mould) ─────────────
  { key: 'mb_yeast',   organism: 'Saccharomyces cerevisiae', coloration: 'methylene_blue', kind: 'yeast',    count: 20 },
  { key: 'mb_mold1',                                          coloration: 'methylene_blue', kind: 'mould',    count: 10 },
  { key: 'mb_mold2',                                          coloration: 'methylene_blue', kind: 'mould',    count: 10 },
  { key: 'mb_mold3',                                          coloration: 'methylene_blue', kind: 'mould',    count: 10 },
  { key: 'mb_ecoli',   organism: 'Escherichia coli',         coloration: 'methylene_blue', kind: 'bacteria', count: 10 },
  { key: 'mb_saureus', organism: 'Staphylococcus aureus',    coloration: 'methylene_blue', kind: 'bacteria', count: 10 },
];

export const TEST_BANK: TestBankSample[] = SPECIES.flatMap((s) =>
  Array.from({ length: s.count }, (_, i) => {
    const n = String(i + 1).padStart(3, '0');
    return {
      id: `${s.key}-${n}`,
      organism: s.organism,
      coloration: s.coloration,
      imageUrl: `${S3}/${s.key}_80X_${n}.jpg`,
      results: {
        gram: s.gram ?? 'uncertain',
        shape: s.shape ?? 'uncertain',
        kind: s.kind,
      },
    } as TestBankSample;
  }),
);
